/** Client-side fuzzy matching for the omnibox / quick switcher (channels, people, agents, commands). */

/** Lowercase, without diacritics ("São" → "sao"). */
export function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

function isSubsequence(q: string, t: string): boolean {
  let i = 0;
  for (let j = 0; j < t.length && i < q.length; j++) if (t.charAt(j) === q.charAt(i)) i += 1;
  return i === q.length;
}

/**
 * Relevance of `text` for `query` (0 = no match): exact 100, prefix 80, word prefix 60, substring 40,
 * in-order subsequence 10. An empty query matches everything with 1.
 */
export function matchScore(query: string, text: string): number {
  const q = fold(query).trim();
  if (q === "") return 1;
  const t = fold(text);
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  for (const w of t.split(/[^\p{L}\p{N}]+/u)) if (w !== "" && w.startsWith(q)) return 60;
  if (t.includes(q)) return 40;
  return isSubsequence(q.replace(/\s+/g, ""), t) ? 10 : 0;
}

export type Candidate = { id: string; text: string; alt?: string };

/**
 * Ids of matching candidates, best first (score, then text, then id). `alt` is a secondary text (full name,
 * topic) that counts when it matches as a substring or better, one point below the same match on `text`.
 */
export function rank(query: string, candidates: Candidate[], limit: number): string[] {
  const scored: { id: string; score: number; text: string }[] = [];
  for (const c of candidates) {
    // Secondary text only counts for real (substring or better) matches, slightly below the primary text.
    const alt = c.alt === undefined ? 0 : matchScore(query, c.alt);
    const score = Math.max(matchScore(query, c.text), alt >= 40 ? alt - 1 : 0);
    if (score > 0) scored.push({ id: c.id, score, text: fold(c.text) });
  }
  scored.sort((a, b) => b.score - a.score || (a.text < b.text ? -1 : a.text > b.text ? 1 : 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return scored.slice(0, Math.max(0, limit)).map((s) => s.id);
}
